import {
  ArrowLeftOutlined,
  FlagOutlined,
  GiftOutlined,
  LockOutlined,
  PlayCircleOutlined,
  StepForwardOutlined,
  TeamOutlined,
  UnlockOutlined,
} from '@ant-design/icons';
import {
  Button,
  Card,
  Col,
  Empty,
  message,
  Popconfirm,
  Row,
  Space,
  Spin,
  Statistic,
  Tag,
  Typography,
} from 'antd';
import type { TFunction } from 'i18next';
import type React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { raffleErrorKey, raffleGet, rafflePost } from '../config/raffleApi';
import {
  getRaffleSocket,
  RAFFLE_EVENTS,
  type RaffleParticipantJoinedPayload,
  type RaffleStateChangedPayload,
  subscribeToDraw,
} from '../config/socket';
import RaffleWinnerPanel from './RaffleWinnerPanel';

const { Title, Text } = Typography;

/**
 * The live control panel (#92 point 3).
 *
 * This is pressed on a lectern, in front of a room, between two sentences of a
 * host's script — so two things drive the whole design. **Only the legal button
 * is enabled**, derived from the same table the server enforces
 * (`raffles/raffle-state.ts`), so the organizer never has to remember which of
 * seven buttons is the right one. And **every rejection still speaks**: the
 * server is the authority, the enablement below is only a convenience, so a
 * refused press is translated from the response `code` and shown — never
 * swallowed because "the button should not have been pressable".
 */

interface RafflePrizeSummary {
  id: string;
  name: string;
  order: number;
  status: 'PENDING' | 'DRAWING' | 'AWARDED';
}

interface RaffleDrawDetail {
  id: string;
  name: string;
  status: 'READY' | 'OPEN' | 'CLOSED' | 'DRAWING' | 'COMPLETED';
  identityField: 'EMAIL' | 'PHONE';
  scheduledAt: string;
  RafflePrize: RafflePrizeSummary[];
  _count: {
    RaffleEntry: number;
  };
}

interface RaffleWinnerRow {
  id: string;
  prizeId: string;
  playerName: string;
  status: 'DRAWN' | 'CONFIRMED' | 'NO_RESPONSE';
  drawnAt: string;
}

interface RaffleControlPanelProps {
  drawId: string;
  onBack: () => void;
  /** Let the dashboard refresh its cards when a round moves. */
  onChanged?: () => void;
}

/**
 * How often the panel re-reads the round when the socket has said nothing.
 *
 * The safety net UNDER the live channel, not a replacement for it. Server ->
 * client frames are not buffered across a disconnect: every `state.changed`,
 * `participant.joined` and `draw.started` emitted while this panel was offline
 * is gone for good. The re-fetch on `connect` below recovers from a blip, but
 * it cannot help when there is no connect at all — a websocket the venue's
 * proxy refuses, a socket that is up but was never re-added to the room — and
 * on conference wifi that is the normal case, not the exotic one. Without this
 * the organizer drives the round against a screen that froze silently.
 *
 * Twelve seconds is two REST calls a tick: five ticks a minute against each of
 * the two endpoints, an order of magnitude inside the 60/min `default`
 * throttler bucket in the backend's `app.module.ts`, and short enough that a
 * change made from a second device lands within one sentence of the host's
 * script.
 */
const STATE_POLL_MS = 12_000;

const drawStatusConfig = {
  READY: { color: 'default', labelKey: 'raffle.status.ready' },
  OPEN: { color: 'green', labelKey: 'raffle.status.open' },
  CLOSED: { color: 'orange', labelKey: 'raffle.status.closed' },
  DRAWING: { color: 'processing', labelKey: 'raffle.status.drawing' },
  COMPLETED: { color: 'default', labelKey: 'raffle.status.completed' },
} as const;

/**
 * What the FINISH confirmation warns about. A module-level function rather
 * than a ternary in the JSX: the panel is already at the top of what Biome
 * allows for one function, and this decision has nothing to do with the rest
 * of it.
 */
function finishDescription(t: TFunction, pendingWinner?: string): string {
  return pendingWinner
    ? t('raffle.controlPanel.confirmFinishPending', { playerName: pendingWinner })
    : t('raffle.controlPanel.confirmFinishBody');
}

const RaffleControlPanel: React.FC<RaffleControlPanelProps> = ({ drawId, onBack, onChanged }) => {
  const { t } = useTranslation();
  const [draw, setDraw] = useState<RaffleDrawDetail | null>(null);
  const [winners, setWinners] = useState<RaffleWinnerRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [acting, setActing] = useState(false);
  const [participantCount, setParticipantCount] = useState(0);
  /** The last refresh failed — the panel is showing a possibly outdated round. */
  const [stale, setStale] = useState(false);

  /**
   * Re-reads the round.
   *
   * `silent` is what a BACKGROUND refresh passes — the poll, the socket
   * handlers and the reconnect. Those are not something the organizer asked
   * for, so a failure must not raise a toast: measured with the backend stopped
   * for twenty seconds, the poll buried the entire control panel under about
   * sixteen stacked error toasts and the buttons could not be reached at all.
   * On stage that is worse than the outage it was reporting. The panel keeps
   * showing its last known state and says so quietly in the header instead,
   * the way the stage screen does.
   *
   * A refresh the organizer triggered (opening the panel, or the reload after
   * an action) still speaks up, because there the silence would be the
   * confusing part.
   */
  const fetchState = useCallback(
    async (silent = false) => {
      try {
        const [drawData, winnerData] = await Promise.all([
          raffleGet<RaffleDrawDetail>(`/raffle/draws/${drawId}`),
          raffleGet<RaffleWinnerRow[]>(`/raffle/draws/${drawId}/winners`),
        ]);
        setDraw(drawData);
        setWinners(winnerData);
        setStale(false);
        // Entries are only ever added within a round, so the freshest of the two
        // sources wins. Without the max, a REST response that left the server
        // before the last join would visibly walk the counter backwards on the
        // organizer's screen while people are still scanning the QR.
        setParticipantCount((current) => Math.max(current, drawData._count?.RaffleEntry ?? 0));
      } catch (error) {
        setStale(true);
        if (!silent) message.error(t(raffleErrorKey(error)));
      }
    },
    [drawId, t]
  );

  useEffect(() => {
    setLoading(true);
    // A different round starts from zero rather than inheriting the previous
    // round's high-water mark through the `Math.max` above.
    setParticipantCount(0);
    fetchState().finally(() => setLoading(false));
  }, [fetchState]);

  useEffect(() => {
    const socket = getRaffleSocket();

    /**
     * (Re)join the ONE room this panel needs and re-read what it missed.
     *
     * Two things happen on a reconnect, and the old handler only did the first.
     * A reconnect issues a NEW socket id and socket.io does not restore rooms
     * for us, so the subscription has to be made again — but the frames emitted
     * while the panel was offline are NOT replayed, so the round it comes back
     * to may already be two states further on. A panel that only re-subscribed
     * would sit on a stale screen and let the organizer press buttons the
     * server has long since moved past.
     *
     * Only the draw room: the gateway sends every event to both rooms, so the
     * event room adds no frame this round needs — it only adds the OTHER
     * rounds of the conference, whose `participant.joined` frames carry a bare
     * `{ count }` (#89 point 3) that this panel cannot tell apart from its own.
     */
    const handleConnect = () => {
      subscribeToDraw(drawId);
      fetchState(true);
    };

    const handleJoined = (payload: RaffleParticipantJoinedPayload) => {
      setParticipantCount((current) => Math.max(current, payload.count));
    };

    // Every other event is only a nudge — the round is re-read over REST
    // instead of being patched from the payload, so the panel and the server
    // cannot drift apart while somebody is pressing buttons against it.
    const handleStateChanged = (payload: RaffleStateChangedPayload) => {
      if (payload.drawId === drawId) fetchState(true);
    };
    const handleDrawEvent = (payload: { drawId: string }) => {
      if (payload.drawId === drawId) fetchState(true);
    };

    // Not `handleConnect`: the mount effect above is already fetching, and the
    // socket is a shared singleton that is usually connected by the time a
    // second round is opened, so `connect` may never fire for this mount.
    subscribeToDraw(drawId);

    socket.on('connect', handleConnect);
    socket.on(RAFFLE_EVENTS.PARTICIPANT_JOINED, handleJoined);
    socket.on(RAFFLE_EVENTS.STATE_CHANGED, handleStateChanged);
    socket.on(RAFFLE_EVENTS.PRIZE_SHOWN, handleDrawEvent);
    socket.on(RAFFLE_EVENTS.DRAW_STARTED, handleDrawEvent);
    socket.on(RAFFLE_EVENTS.WINNER_CONFIRMED, handleDrawEvent);

    const poll = setInterval(() => {
      fetchState(true);
    }, STATE_POLL_MS);

    return () => {
      clearInterval(poll);
      socket.off('connect', handleConnect);
      socket.off(RAFFLE_EVENTS.PARTICIPANT_JOINED, handleJoined);
      socket.off(RAFFLE_EVENTS.STATE_CHANGED, handleStateChanged);
      socket.off(RAFFLE_EVENTS.PRIZE_SHOWN, handleDrawEvent);
      socket.off(RAFFLE_EVENTS.DRAW_STARTED, handleDrawEvent);
      socket.off(RAFFLE_EVENTS.WINNER_CONFIRMED, handleDrawEvent);
    };
  }, [drawId, fetchState]);

  const prizes = useMemo(() => draw?.RafflePrize ?? [], [draw]);

  /** The prize currently on the wall, if the organizer has shown one. */
  const currentPrize = useMemo(
    () => prizes.find((prize) => prize.status === 'DRAWING') ?? null,
    [prizes]
  );

  /** The next prize that has not been handed out yet. */
  const nextPrize = useMemo(
    () => prizes.find((prize) => prize.status === 'PENDING') ?? null,
    [prizes]
  );

  /**
   * The winner waiting for a decision. `DRAWN` is the only status that means
   * "somebody has been pulled and nobody has confirmed them yet" — and it is
   * the only state in which the real identity may be fetched at all.
   */
  const activeWinner = useMemo(
    () => winners.find((winner) => winner.status === 'DRAWN') ?? null,
    [winners]
  );

  const hasAwarded = prizes.some((prize) => prize.status === 'AWARDED');
  const status = draw?.status;

  // The enablement table, read straight off the state machine in
  // `raffles/raffle-state.ts` plus the prize/winner facts above.
  const canOpen = status === 'READY';
  const canClose = status === 'OPEN';
  const canShowPrize =
    (status === 'CLOSED' || status === 'DRAWING') &&
    !currentPrize &&
    !activeWinner &&
    Boolean(nextPrize);
  const canStartDraw = status === 'DRAWING' && Boolean(currentPrize) && !activeWinner;
  const canFinish = status === 'CLOSED' || status === 'DRAWING';

  const runAction = async (action: () => Promise<unknown>) => {
    setActing(true);
    try {
      await action();
      await fetchState();
      onChanged?.();
    } catch (error) {
      // The server is the authority; a rejection is shown in the ADMIN's
      // language, mapped from `code`. The response `message` is never read —
      // it arrives in the event's language (see `config/raffleApi.ts`).
      message.error(t(raffleErrorKey(error)));
    } finally {
      setActing(false);
    }
  };

  const handleOpen = () =>
    runAction(async () => {
      await rafflePost(`/raffle/draws/${drawId}/open`);
      message.success(t('raffle.messages.opened'));
    });

  const handleClose = () =>
    runAction(async () => {
      await rafflePost(`/raffle/draws/${drawId}/close`);
      message.success(t('raffle.messages.closed'));
    });

  const handleShowPrize = () =>
    runAction(async () => {
      if (!nextPrize) return;
      await rafflePost(`/raffle/draws/${drawId}/prizes/${nextPrize.id}/show`);
      message.success(t('raffle.messages.prizeShown'));
    });

  const handleStartDraw = () =>
    runAction(async () => {
      if (!currentPrize) return;
      await rafflePost(`/raffle/draws/${drawId}/prizes/${currentPrize.id}/draw`);
      message.success(t('raffle.messages.drawStarted'));
    });

  const handleFinish = () =>
    runAction(async () => {
      await rafflePost(`/raffle/draws/${drawId}/finish`);
      message.success(t('raffle.messages.finished'));
    });

  if (loading && !draw) {
    return (
      <div style={{ textAlign: 'center', padding: 48 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!draw) {
    return <Empty description={t('raffle.errors.drawNotFound')} />;
  }

  const statusConfig = drawStatusConfig[draw.status];

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Space>
        <Button icon={<ArrowLeftOutlined />} onClick={onBack}>
          {t('common.back')}
        </Button>
      </Space>

      <Card>
        <Row gutter={[24, 24]} align="middle">
          <Col xs={24} md={12}>
            <Text type="secondary">{t('raffle.controlPanel.round')}</Text>
            <Title level={2} style={{ margin: '4px 0 12px' }}>
              {draw.name}
            </Title>
            <Space size="middle" wrap>
              <span>
                <Text type="secondary">{t('common.status')}: </Text>
                <Tag color={statusConfig.color} style={{ fontSize: 15, padding: '4px 12px' }}>
                  {t(statusConfig.labelKey)}
                </Tag>
              </span>
              {/* One quiet line instead of a toast per failed background
                  refresh. The organizer needs to know the screen may be behind
                  the server, not to have the buttons covered up. */}
              {stale && (
                <Tag color="warning" style={{ fontSize: 13 }}>
                  {t('raffle.controlPanel.stale')}
                </Tag>
              )}
            </Space>
          </Col>
          <Col xs={24} md={12}>
            {/* Updated live from `participant.joined` — no reload while the
                room is scanning the QR. */}
            <Statistic
              title={
                <Space>
                  <TeamOutlined />
                  {t('raffle.controlPanel.participants')}
                </Space>
              }
              value={participantCount}
              valueStyle={{ fontSize: 44, fontWeight: 600 }}
            />
          </Col>
        </Row>
      </Card>

      <Card title={t('raffle.controlPanel.prizes')} size="small">
        {prizes.length === 0 ? (
          <Empty description={t('raffle.prizes.empty')} />
        ) : (
          <Space wrap size="middle">
            {prizes.map((prize, index) => {
              const isCurrent = currentPrize?.id === prize.id;
              const isNext = !currentPrize && nextPrize?.id === prize.id;
              return (
                <Tag
                  key={prize.id}
                  color={
                    prize.status === 'AWARDED' ? 'success' : isCurrent ? 'processing' : 'default'
                  }
                  style={{
                    fontSize: 15,
                    padding: '6px 12px',
                    borderWidth: isCurrent || isNext ? 2 : 1,
                  }}
                >
                  <Space size={6}>
                    <GiftOutlined />
                    {`${index + 1}. ${prize.name}`}
                    {isCurrent && <Text strong>{t('raffle.controlPanel.onStage')}</Text>}
                    {isNext && <Text type="secondary">{t('raffle.controlPanel.next')}</Text>}
                  </Space>
                </Tag>
              );
            })}
          </Space>
        )}
      </Card>

      <Card size="small">
        <Space size="middle" wrap>
          <Button
            type="primary"
            size="large"
            icon={<UnlockOutlined />}
            disabled={!canOpen || acting}
            onClick={handleOpen}
          >
            {t('raffle.actions.openRegistration')}
          </Button>

          {/* Closing is one-way — `raffle-state.ts` forbids CLOSED -> OPEN, so
              a mis-click here cannot be undone and the room can no longer join. */}
          <Popconfirm
            title={t('raffle.controlPanel.confirmCloseTitle')}
            description={t('raffle.controlPanel.confirmCloseBody')}
            onConfirm={handleClose}
            okText={t('common.yes')}
            cancelText={t('common.no')}
            disabled={!canClose || acting}
          >
            <Button size="large" icon={<LockOutlined />} disabled={!canClose || acting}>
              {t('raffle.actions.closeRegistration')}
            </Button>
          </Popconfirm>

          <Button
            type={canShowPrize && !hasAwarded ? 'primary' : 'default'}
            size="large"
            icon={<GiftOutlined />}
            disabled={!canShowPrize || hasAwarded || acting}
            onClick={handleShowPrize}
          >
            {t('raffle.actions.showPrize')}
          </Button>

          <Button
            type="primary"
            size="large"
            icon={<PlayCircleOutlined />}
            disabled={!canStartDraw || acting}
            onClick={handleStartDraw}
          >
            {t('raffle.actions.startDraw')}
          </Button>

          {/* Same call as SHOW PRIZE, separated so that exactly one button is
              lit at each step: this is the one the host reaches for after a
              winner has been confirmed. */}
          <Button
            type={canShowPrize && hasAwarded ? 'primary' : 'default'}
            size="large"
            icon={<StepForwardOutlined />}
            disabled={!(canShowPrize && hasAwarded) || acting}
            onClick={handleShowPrize}
          >
            {t('raffle.actions.nextPrize')}
          </Button>

          {/* An unconfirmed winner is the one thing FINISH cannot be undone
              for: the round is terminal, so `confirmWinner` — which requires a
              DRAWING round — can never run again, and the prize stays
              `DRAWING` with nobody holding it (#174). Finishing anyway stays
              possible: a winner who never came forward is exactly when the
              host has to move on. */}
          <Popconfirm
            title={t('raffle.controlPanel.confirmFinishTitle')}
            description={finishDescription(t, activeWinner?.playerName)}
            onConfirm={handleFinish}
            okText={t('common.yes')}
            cancelText={t('common.no')}
            disabled={!canFinish || acting}
          >
            <Button size="large" icon={<FlagOutlined />} disabled={!canFinish || acting}>
              {t('raffle.actions.finishRound')}
            </Button>
          </Popconfirm>
        </Space>
      </Card>

      {/* Mounted only once somebody is actually waiting to be confirmed — that
          is what gates the single identity-revealing request. */}
      {activeWinner && (
        <RaffleWinnerPanel
          drawId={drawId}
          winnerId={activeWinner.id}
          playerName={activeWinner.playerName}
          roundClosed={status === 'COMPLETED'}
          onResolved={() => {
            fetchState(true);
            onChanged?.();
          }}
        />
      )}
    </Space>
  );
};

export default RaffleControlPanel;
